import { useMutation, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Breadcrumb from "../Breadcrumb";
import Card from "../Card";
import Spinner from "../Spinner";
import { useToast } from "../Toast/toastHook";
import Modal from "../modal/Modal";
import { GET_ALL_SERVICES, DELETE_SERVICE } from "../../graphql/serviceQueries";
import { formatDate } from "../../utils/dateFormat";
import CompanyDirectoryForm from "./CompanyServiceForm";
import ServiceEditView from "../directory/ServiceEditView";
import { Button, Table } from "antd";
import {
  PlusOutlined as PlusIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
} from "@ant-design/icons";
import useDebounce from "../../hooks/useDebounce";
import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import TableToolbar from "../TableToolbar/TableToolbar";
import { toServerSort } from "../../utils/tableSort";
import { TABLE_SCROLL } from "../../constants/table";

// Columns the server can order the services by.
const SORTABLE_COLUMNS = ["title", "createdAt", "updatedAt"];

const CompanyDirectoriesListing = ({ companyId }) => {
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalCount: 0,
  });

  const [serviceListing, setServiceListing] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [formType, setFormType] = useState("add");
  // For edit
  const [service, setService] = useState(null);
  const [serviceTitle, setServiceTitle] = useState(null);
  const [searchText, setSearchText] = useState("");
  const debounceValue = useDebounce(searchText, 500);
  const [sort, setSort] = useState(null);
  const toast = useToast(4000);
  const { confirm, dialog } = useConfirmDialog();

  const {
    data,
    loading: isPending,
    error: isError,
    refetch: refetchService,
  } = useQuery(GET_ALL_SERVICES, {
    fetchPolicy: "no-cache",
    variables: {
      companyId,
      pagination: {
        page: pagination.page,
        limit: pagination.limit,
      },
      searchText: debounceValue,
      ...(sort && { sort }),
    },
  });

  const [deleteService] = useMutation(DELETE_SERVICE);

  const handleEdit = (serviceForEdit) => {
    setService(serviceForEdit._id);
    setServiceTitle(serviceForEdit.title);
    setFormType("edit");
    setShowEditModal(true);
  };

  const handleDelete = async (serviceId) => {
    try {
      const { data } = await deleteService({
        variables: { serviceId },
      });
      refetchService();
      toast(
        "success",
        `${data.deleteService.title} service deleted successfully.`
      );
    } catch (error) {
      toast("error", error.message);
    }
  };

  const handleModalRender = () => {
    setFormType("add");
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const onChange = (nextPagination, _tableFilters, sorter, extra) => {
    // A new sort order starts again from the first page.
    if (extra?.action === "sort") {
      setSort(toServerSort(sorter, SORTABLE_COLUMNS));
      setPagination((prev) => ({ ...prev, page: 1 }));
      return;
    }
    setPagination((prev) => ({
      ...prev,
      page: nextPagination?.current,
      limit: nextPagination?.pageSize,
    }));
  };

  const columns = [
    {
      title: "Name",
      dataIndex: "title",
      key: "title",
      sorter: true,
      render: (text, record) => (
        <Link to={`/company/${companyId}/services/${record._id}`}>
          <div className="text-black flex flex-row items-center space-x-4">
            <div>{text}</div>
          </div>
        </Link>
      ),
    },
    {
      title: "Date Created",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      render: (date) => (
        <span className="font-medium text-black">{formatDate(date)}</span>
      ),
    },
    {
      title: "Date Updated",
      dataIndex: "updatedAt",
      key: "updatedAt",
      sorter: true,
      render: (date) => (
        <span className="font-medium text-black">{formatDate(date)}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <div className="flex items-center space-x-2">
          <Link
            to={`/company/${companyId}/services/${record._id}?name=${record.title}`}
          >
            <Button
              className="border-secondary text-secondary"
              icon={<PlusIcon />}
            />
          </Link>

          <Button
            className="border-primary text-primary"
            icon={<EditIcon />}
            onClick={() => handleEdit(record)}
          />
          <Button
            danger
            icon={<DeleteIcon />}
            onClick={() =>
              confirm({
                title: `Delete ${record.title}?`,
                description:
                  "This service will be permanently deleted. This action cannot be undone.",
                onConfirm: () => handleDelete(record._id),
              })
            }
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    if (data?.getAllServices?.services)
      setServiceListing(data?.getAllServices?.services);
    setPagination((prev) => ({
      ...prev,
      totalCount: data?.getAllServices?.totalCount,
    }));
  }, [data]);

  return (
    <div>
      <div id="companies">
        <Breadcrumb pageName="Services" />
        <div className="pb-6">
          <Card>
            <TableToolbar
              title="All Services"
              count={pagination.totalCount || 0}
              searchValue={searchText}
              onSearch={setSearchText}
              action={
                <button
                  className="bg-[#B4C540] h-[36px] px-5 text-center rounded-xl text-[#FFFFFF]"
                  onClick={handleModalRender}
                >
                  + Add New Service
                </button>
              }
            />
            <Table
              scroll={TABLE_SCROLL}
              bordered
              rowKey="_id"
              columns={columns}
              dataSource={serviceListing}
              pagination={{
                position: ["bottomCenter"],
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.totalCount,
              }}
              onChange={onChange}
            />
          </Card>
        </div>
        <Modal
          title={"Add New Service"}
          showModal={showModal}
          setShowModal={(value) => {
            setShowModal(value);
          }}
        >
          <CompanyDirectoryForm
            handleClose={handleClose}
            onDirectoryAdded={() => {
              refetchService();
              // refetch();
            }}
            formType={formType}
            companyId={companyId}
            directory={service}
            clickedFrom={"company"}
          />
        </Modal>
        <Modal
          title={"Edit Service"}
          showModal={showEditModal}
          setShowModal={(value) => {
            setShowEditModal(value);
          }}
        >
          <ServiceEditView
            setShowEditModal={setShowEditModal}
            serviceId={service}
            currentTitle={serviceTitle}
            handleClose={() => {
              setShowEditModal(false);
              refetchService();
            }}
            clickedFrom={"company"}
          />
        </Modal>
        {dialog}
      </div>
    </div>
  );
};

export default CompanyDirectoriesListing;
